import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, MARKS} from '@contentful/rich-text-types'

import {Box, Text, UnorderedList} from '@primer/react-brand/esm'
import {CheckIcon, DashIcon, SyncIcon, InfinityIcon} from '@primer/octicons-react'

import {t} from '@github-ui/swp-core/lib/utils/localization'
import type {RichText} from '@github-ui/swp-core/schemas/contentful/richText'

import type {CopilotFeatureStatus} from '../../../../../../brand/lib/types/contentful'

import styles from './styles.module.css'

function renderRichText(richText: RichText) {
  return documentToReactComponents(richText, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <Text size="100">{children}</Text>,
      [BLOCKS.UL_LIST]: (_, children) => <UnorderedList variant="checked">{children}</UnorderedList>,
      [BLOCKS.LIST_ITEM]: node => {
        const nodeContent = node.content.at(0)
        const textContent = nodeContent?.nodeType === BLOCKS.PARAGRAPH ? nodeContent.content.at(0) : undefined

        if (textContent?.nodeType !== 'text') {
          return null
        }

        const isMuted = textContent.marks.some(mark => mark.type === MARKS.STRIKETHROUGH)

        if (isMuted) {
          return (
            <UnorderedList.Item
              leadingVisual={DashIcon}
              leadingVisualAriaLabel={t('Not included')}
              className={styles.muted}
              variant="muted"
            >
              <Text size="100" variant="muted">
                {textContent.value}
              </Text>
            </UnorderedList.Item>
          )
        } else {
          return (
            <UnorderedList.Item leadingVisualFill="var(--brand-color-success-fg)">
              <Text size="100">{textContent.value}</Text>
            </UnorderedList.Item>
          )
        }
      },
    },
  })
}

type Props = {
  name: string
  status: CopilotFeatureStatus
  text?: RichText
}

export function CompareTableCell(props: Props) {
  const {name, status, text} = props

  const renderSwitch = () => {
    switch (status) {
      case 'Disabled':
        return (
          <>
            <DashIcon size={24} className={styles.muted} />
            <span className="sr-only">{t('Not included')}</span>
          </>
        )
      case 'Enabled':
        return (
          <>
            {text ? (
              renderRichText(text)
            ) : (
              <>
                <CheckIcon size={24} className={styles.success} />
                <span className="sr-only">{t('Included')}</span>
              </>
            )}
          </>
        )
      case 'Refresh':
        return (
          <>
            <SyncIcon size={24} className={styles.refreshIcon} />

            <div className={styles.text}>{text ? renderRichText(text) : <Text size="100">{t('Refresh')}</Text>}</div>
          </>
        )
      case 'Unlimited':
        return (
          <>
            <InfinityIcon size={24} className={styles.unlimitedIcon} />

            <div className={styles.text}>{text ? renderRichText(text) : <Text size="100">{t('Unlimited')}</Text>}</div>
          </>
        )
      default:
        return null
    }
  }

  return (
    <Box role="cell" className={styles.root}>
      <Text size="100" weight="semibold" variant="muted" className={styles.planName}>
        {name} <span className="sr-only">plan</span>
      </Text>

      <div className={styles.content}>{renderSwitch()}</div>
    </Box>
  )
}
